/**
 * Button component with StyleX styling.
 * Supports variants matching Nova aesthetic with Indigo theme.
 */

import * as stylex from "@stylexjs/stylex";

import { colors, fontSize, radius, shadow, space } from "../tokens.stylex";

/** Icon button dimensions by size */
const iconSize = {
  sm: 28,
  default: 36,
  lg: 44,
} as const;

const styles = stylex.create({
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: space.sm,
    whiteSpace: "nowrap",
    borderRadius: radius.md,
    fontWeight: 500,
    fontSize: fontSize.sm,
    cursor: "pointer",
    borderWidth: 0,
    borderStyle: "none",
    transitionProperty: "background-color, color, border-color, opacity",
    transitionDuration: "150ms",
    ":disabled": {
      opacity: 0.5,
      pointerEvents: "none",
    },
    ":focus-visible": {
      outline: "2px solid",
      outlineColor: colors.ring,
      outlineOffset: "2px",
    },
  },
  iconOnly: {
    padding: 0,
  },
  iconOnlySm: {
    width: iconSize.sm,
    height: iconSize.sm,
  },
  iconOnlyDefault: {
    width: iconSize.default,
    height: iconSize.default,
  },
  iconOnlyLg: {
    width: iconSize.lg,
    height: iconSize.lg,
  },
  primary: {
    backgroundColor: colors.primary,
    color: colors.primaryForeground,
    boxShadow: shadow.sm,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      backgroundColor: colors.primaryHover,
    },
  },
  secondary: {
    backgroundColor: colors.secondary,
    color: colors.secondaryForeground,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      backgroundColor: colors.borderSubtle,
    },
  },
  destructive: {
    backgroundColor: colors.destructive,
    color: colors.destructiveForeground,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      opacity: 0.9,
    },
  },
  outline: {
    backgroundColor: colors.transparent,
    color: colors.foreground,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.borderSubtle,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      backgroundColor: colors.muted,
      borderColor: colors.border,
    },
  },
  ghost: {
    backgroundColor: colors.transparent,
    color: colors.foregroundMuted,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      backgroundColor: colors.muted,
      color: colors.foreground,
    },
  },
  link: {
    backgroundColor: colors.transparent,
    color: colors.primary,
    borderWidth: 0,
    borderStyle: "none",
    padding: 0,
    cursor: "pointer",
    ":hover": {
      color: colors.primaryHover,
      textDecoration: "underline",
    },
  },
  danger: {
    backgroundColor: colors.destructive,
    color: colors.destructiveForeground,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      opacity: 0.9,
    },
  },
  success: {
    backgroundColor: colors.success,
    color: colors.successForeground,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      opacity: 0.9,
    },
  },
  warning: {
    backgroundColor: colors.warning,
    color: colors.warningForeground,
    paddingInline: space.lg,
    paddingBlock: space.sm,
    ":hover": {
      opacity: 0.9,
    },
  },
  sm: {
    paddingInline: space.md,
    paddingBlock: space.xs,
    fontSize: fontSize.xs,
    borderRadius: radius.sm,
  },
  default: {},
  lg: {
    paddingInline: space.xl,
    paddingBlock: space.md,
    fontSize: fontSize.md,
    borderRadius: radius.lg,
  },
  full: {
    width: "100%",
  },
  small: {
    paddingInline: space.md,
    paddingBlock: space.xs,
    fontSize: fontSize.xs,
    borderRadius: radius.sm,
  },
});

export const button = styles;

type ButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "outline"
  | "ghost"
  | "link"
  | "danger"
  | "success"
  | "warning";

type ButtonSize = "sm" | "default" | "lg" | "small";

interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  xstyle?: stylex.StyleXStyles;
  /** Icon or symbol rendered before children */
  startSymbol?: React.ReactNode;
  /** Icon or symbol rendered after children */
  endSymbol?: React.ReactNode;
}

/** Icon-only size style lookup */
const iconOnlyStyles = {
  sm: styles.iconOnlySm,
  default: styles.iconOnlyDefault,
  lg: styles.iconOnlyLg,
  small: styles.iconOnlySm,
} as const;

function Button({
  variant = "primary",
  size = "default",
  full,
  xstyle,
  startSymbol,
  endSymbol,
  children,
  ...props
}: ButtonProps) {
  const isIconOnly =
    startSymbol != null && children == null && endSymbol == null;

  return (
    <button
      {...stylex.props(
        styles.base,
        styles[variant],
        size !== "default" && styles[size],
        full && styles.full,
        isIconOnly && styles.iconOnly,
        isIconOnly && iconOnlyStyles[size],
        xstyle
      )}
      {...props}
    >
      {startSymbol}
      {children}
      {endSymbol}
    </button>
  );
}

export { Button, type ButtonProps, type ButtonVariant, type ButtonSize };
